import { FLAG_ICONS } from './icons'

export type WidgetSize = 'S' | 'M' | 'L'
export type Position = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
export type TextAlignment = 'left' | 'center' | 'right' | 'default'

export type AccessibilityProfile =
  | 'seizure-safe'
  | 'vision-impaired'
  | 'adhd-friendly'
  | 'cognitive-disability'
  | 'keyboard-navigation'
  | 'screen-reader'
  | 'color-blind'
  | 'dyslexia'

export type ColorScheme = 'light' | 'dark'
export type Lang = 'en' | 'ar' | 'es' | 'fr' | 'de' | 'pt'

export type TriggerScheme = 'auto' | 'dark' | 'light' | 'blue'

export interface AccessifyConfig {
  position?: Position
  size?: WidgetSize
  theme?: {
    primary?: string
    background?: string
    text?: string
    headerBackground?: string
  }
  colorScheme?: ColorScheme
  /**
   * Trigger button color preset.
   * - 'auto'  — matches colorScheme (default)
   * - 'dark'  — black background, white icon
   * - 'light' — white background, dark icon
   * - 'blue'  — blue background (#0066ff), white icon (standard a11y preset)
   */
  triggerScheme?: TriggerScheme
  /** Custom SVG markup for the trigger button icon */
  triggerIcon?: string
  /** Brand title displayed in widget header. Defaults to 'Tech unicorn' */
  brandTitle?: string
  persistence?: boolean
  lang?: Lang
  onOpen?: () => void
  onClose?: () => void
  onReset?: () => void
  onLangChange?: (lang: Lang) => void
  onColorSchemeChange?: (scheme: ColorScheme) => void
  onSizeChange?: (size: WidgetSize) => void
}

export interface SupportedLang {
  code: Lang
  label: string
  flag: string
  emoji?: string
}

export const SUPPORTED_LANGS: SupportedLang[] = [
  { code: 'en', label: 'ENGLISH (US)', flag: FLAG_ICONS.en, emoji: '🇺🇸' },
  { code: 'es', label: 'ESPAÑOL', flag: FLAG_ICONS.es, emoji: '🇪🇸' },
  { code: 'fr', label: 'FRANÇAIS', flag: FLAG_ICONS.fr, emoji: '🇫🇷' },
  { code: 'de', label: 'DEUTSCH', flag: FLAG_ICONS.de, emoji: '🇩🇪' },
  { code: 'pt', label: 'PORTUGUÊS', flag: FLAG_ICONS.pt, emoji: '🇵🇹' },
  { code: 'ar', label: 'العربية', flag: FLAG_ICONS.ar, emoji: '🇸🇦' },
]

export interface AccessifyState {
  profile: AccessibilityProfile | null
  fontSize: number
  contentScale: number
  lineHeight: number
  letterSpacing: number
  textAlignment: TextAlignment
  readableFont: boolean
  highlightTitles: boolean
  highlightLinks: boolean
  textMagnifier: boolean
  readingLens: boolean
  darkContrast: boolean
  lightContrast: boolean
  highContrast: boolean
  colorBlind: boolean
  monochrome: boolean
  invertColors: boolean
  highSaturation: boolean
  lowSaturation: boolean
  textColor: string | null
  titleColor: string | null
  backgroundColor: string | null
  readingGuide: boolean
  readingMask: boolean
  stopAnimations: boolean
  highlightHover: boolean
  bigBlackCursor: boolean
  bigWhiteCursor: boolean
}

export const DEFAULT_STATE: AccessifyState = {
  profile: null,
  fontSize: 0,
  contentScale: 0,
  lineHeight: 0,
  letterSpacing: 0,
  textAlignment: 'default',
  readableFont: false,
  highlightTitles: false,
  highlightLinks: false,
  textMagnifier: false,
  readingLens: false,
  darkContrast: false,
  lightContrast: false,
  highContrast: false,
  colorBlind: false,
  monochrome: false,
  invertColors: false,
  highSaturation: false,
  lowSaturation: false,
  textColor: null,
  titleColor: null,
  backgroundColor: null,
  readingGuide: false,
  readingMask: false,
  stopAnimations: false,
  highlightHover: false,
  bigBlackCursor: false,
  bigWhiteCursor: false,
}

export const STORAGE_KEY = 'accessify-state'
